import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Calendar, Trash2, Plus, AlertCircle } from "lucide-react";
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { PriceMaster } from "@/types/system-setting/priceMaster";

interface SpecialPricingProps {
  control: Control<PriceMaster>;
  errors: FieldErrors<PriceMaster>;
  watch: UseFormWatch<PriceMaster>;
  setValue: UseFormSetValue<PriceMaster>;
  readOnly?: boolean;
}

const SpecialPricingComp = ({ control, errors, watch, setValue, readOnly = false }: SpecialPricingProps) => {
  const isEnabled = watch("enable_special_pricing");
  const specialPricing = watch("special_pricing") || [];

  useEffect(() => {
    if (specialPricing.length > 0 && !isEnabled) {
      setValue("enable_special_pricing", true);
    }
  }, [specialPricing, isEnabled, setValue]);

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Calendar className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Special Pricing</h3>
            <p className="text-xs text-gray-600">Create time-based promotional pricing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Enable</span>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                setValue("enable_special_pricing", checked);
                if (checked) {
                  if (specialPricing.length === 0) {
                    setValue("special_pricing", [{ name: "", price: 0, date_from: "", date_to: "" }]);
                  }
                } else {
                  setValue("special_pricing", []);
                }
              }}
              disabled={readOnly}
            />
        </div>
      </div>

      {isEnabled && (
        <div className="space-y-4 mt-4">
          <Controller
            control={control}
            name="special_pricing"
            rules={{
              validate: {
                specialValid: (rules) => {
                  if (!rules || rules.length === 0) return true;
                  for (let i = 0; i < rules.length; i++) {
                    if (!rules[i].name) return `Special Rule ${i + 1}: Name is required`;
                    if (rules[i].price <= 0) return `Special Rule ${i + 1}: Price should be greater than zero`;
                    const dateFrom = rules[i].date_from;
                    const dateTo = rules[i].date_to;

                    if (!dateFrom || !dateTo) return `Special Rule ${i + 1}: Both dates are required`;

                    const today = new Date().toISOString().split('T')[0];
                    if (dateFrom < today) {
                      return `Special Rule ${i + 1}: Start date cannot be in the past`;
                    }
                    if (dateTo < dateFrom) {
                      return `Special Rule ${i + 1}: End date must be after or equal to start date`;
                    }
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className="space-y-4">
                {error && <p className="text-red-500 text-sm">{error.message}</p>}
                {field.value?.map((rule, index) => (
                  <Card key={index} className="p-4 bg-white border-2 border-amber-100 hover:border-amber-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                        Rule {index + 1}
                      </Badge>
                      {(field.value?.length || 0) > 1 && !readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newRules = [...(field.value || [])];
                            newRules.splice(index, 1);
                            field.onChange(newRules);
                          }}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Rule Name *</label>
                        <Input
                          placeholder="Early Bird"
                          value={rule.name}
                          onChange={(e) => {
                            const newRules = [...(field.value || [])];
                            newRules[index].name = e.target.value;
                            field.onChange(newRules);
                          }}
                          disabled={readOnly}
                          className="h-9"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Price *</label>
                        <div className="relative">
                          <p className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm">Rs.</p>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={rule.price}
                            className="pl-10 h-9"
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              const newRules = [...(field.value || [])];
                              newRules[index].price = isNaN(value) ? 0 : value;
                              field.onChange(newRules);
                            }}
                            placeholder="0.00"
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">From Date *</label>
                        <div className="relative">
                          <Input
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={rule.date_from}
                            className="h-9 pr-10 [&::-webkit-calendar-picker-indicator]:hidden"
                            onChange={(e) => {
                              const newRules = [...(field.value || [])];
                              newRules[index].date_from = e.target.value;
                              field.onChange(newRules);
                            }}
                            disabled={readOnly}
                          />
                          <Calendar 
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-amber-600 transition-colors" 
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as any;
                              if (input) {
                                if (typeof input.showPicker === "function") {
                                  input.showPicker();
                                } else {
                                  input.focus();
                                }
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">To Date *</label>
                        <div className="relative">
                          <Input
                            type="date"
                            min={rule.date_from || new Date().toISOString().split('T')[0]}
                            value={rule.date_to}
                            className="h-9 pr-10 [&::-webkit-calendar-picker-indicator]:hidden"
                            onChange={(e) => {
                              const newRules = [...(field.value || [])];
                              newRules[index].date_to = e.target.value;
                              field.onChange(newRules);
                            }}
                            disabled={readOnly}
                          />
                          <Calendar 
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-amber-600 transition-colors" 
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as any;
                              if (input) {
                                if (typeof input.showPicker === "function") {
                                  input.showPicker();
                                } else {
                                  input.focus();
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {!readOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-fit border-2 border-amber-300 text-amber-700 hover:bg-amber-50 font-bold"
                    onClick={() => {
                      field.onChange([...(field.value || []), { name: "", price: 0, date_from: "", date_to: "" }]);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Special Pricing Rule
                  </Button>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SpecialPricingComp;
